import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronRight, SquarePen } from 'lucide-react';
import { RobbieMark, RobbieState, Skeleton } from '../ui';
import RobbieChatInput, { RobbieChatInputProps } from './RobbieChatInput';

/**
 * v2 docked Robbie panel — the permanent conversation rail inside the wizard,
 * ported from the `RFQ Wizard v2*.dc.html` mocks: waving-mark header
 * ("Working with you on this RFQ"), NEXT BEST STEP card slot, message
 * bubbles (Robbie left / user right in action blue, except a voice turn,
 * which is a neutral-toned media object — violet stays Robbie-only either
 * way), typing dots, quick-action chip row, chat input with mic.
 * Blue→violet `gradient.aiPanel` wash; width comes from the parent — the
 * wizard's drag-resizable rail (`useResizableRail`), or `w-robbie-rail` (its
 * resting width) elsewhere. Presentational — pages wire the chat behavior.
 */
export interface RobbieChromeMessage {
  id: string;
  content: React.ReactNode;
  /** Defaults to robbie; user messages align right in action blue. */
  role?: 'robbie' | 'user';
  /**
   * `voice` renders on neutral surface/line/ink tokens instead of the
   * action-blue bubble — a voice message is a distinct media object, and
   * violet is off the table too (it's the buyer's own turn, not a Robbie
   * surface). Defaults to `text`.
   */
  variant?: 'text' | 'voice';
}

export interface RobbieQuickAction {
  id: string;
  label: string;
  onSelect?: () => void;
}

export interface RobbieDockedPanelProps {
  /** Mascot state in the header (waving idle-at-your-side; thinking shimmer…). */
  state?: RobbieState;
  subtitle?: string;
  /**
   * Small control on the header's right edge (e.g. "New conversation"). Icon
   * only, labelled: the rail is ~230px at its narrowest and a text button next
   * to the subtitle breaks that line into four.
   */
  headerAction?: { label: string; onSelect: () => void };
  /** NEXT BEST STEP card; omit to hide the slot. */
  nextStep?: { label: string; onSelect?: () => void };
  /**
   * Robbie-voice line for a stage that is currently working. While it is set
   * the slot reports the work instead of offering a next best step: there is
   * no next step mid-run, and the card would be a control that does nothing.
   */
  working?: string;
  /** The wait behind `working` failed: the slot shows the error's title, nothing more. */
  workingFailed?: boolean;
  messages?: RobbieChromeMessage[];
  /**
   * Fills the thread area INSTEAD of the messages — Robbie's resting state
   * when a conversation opens cold. The scrollback is folded away, never
   * dropped; `continueLine` is how it comes back.
   */
  resting?: React.ReactNode;
  /**
   * One quiet row pinned to the top of the footer, above the composer. Shown
   * in the resting state to offer the previous conversation back.
   */
  continueLine?: React.ReactNode;
  /**
   * Bumped when the buyer unfolds the previous thread, so the reveal plays
   * for that one case and not on every ordinary message. The reveal is a
   * `motion-reduce:`-guarded transition — reduced motion gets the thread at
   * once, with no movement.
   */
  unfoldNonce?: number;
  /** Robbie is writing a reply: dots at the end of the thread, cleared the moment it lands. */
  typing?: boolean;
  /** The stored conversation is still loading: message-shaped skeletons, never typing dots. */
  historyLoading?: boolean;
  /** Suggestion chips are refreshing: pill skeletons that never block the thread or the input. */
  chipsLoading?: boolean;
  /** Inline notice row under the thread (e.g. an honest error strip). */
  notice?: React.ReactNode;
  quickActions?: RobbieQuickAction[];
  input?: RobbieChatInputProps;
  /**
   * Replaces the chips and the input entirely. Used when the viewer has
   * nothing to say to Robbie about this RFQ — an input that 403s on send is a
   * control that does nothing, so the note takes its place.
   */
  footerNote?: React.ReactNode;
  className?: string;
}

const TypingDots: React.FC = () => (
  <div role="status" data-testid="robbie-typing" className="flex flex-col gap-s1">
    <div className="flex w-fit items-center gap-[5px] rounded-card border border-robbie-border-soft bg-surface px-[12px] py-s4">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          aria-hidden="true"
          className="h-[5px] w-[5px] motion-safe:animate-bounce rounded-round bg-robbie"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>
    <span className="pl-s1 text-[11px] text-[#8A8FA8]">Robbie is writing…</span>
  </div>
);

const HistorySkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading the conversation" className="mt-auto flex flex-col gap-[10px] pb-[6px]">
    <span className="w-[62%] self-end"><Skeleton shape="bubble" height={30} /></span>
    <Skeleton shape="bubble" width="84%" height={58} />
    <span className="w-[48%] self-end"><Skeleton shape="bubble" height={30} /></span>
    <Skeleton shape="bubble" width="70%" height={40} />
  </div>
);

const ChipSkeletons: React.FC = () => (
  <div aria-hidden="true" data-testid="robbie-chips-loading" className="flex gap-s2">
    <Skeleton width={118} height={24} />
    <Skeleton width={96} height={24} />
  </div>
);

/**
 * Plays the unfold once per nonce: the folded thread fades and settles into
 * place instead of appearing mid-scroll with no explanation of where it came
 * from. `motion-reduce:transition-none` collapses it to an instant swap.
 */
const ThreadReveal: React.FC<{ nonce: number; children: React.ReactNode }> = ({ nonce, children }) => {
  const [settled, setSettled] = useState(nonce === 0);
  useEffect(() => {
    if (nonce === 0) return;
    setSettled(false);
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, [nonce]);
  return (
    <div
      className={`flex flex-col gap-[9px] transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
        settled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-s3'
      }`}
    >
      {children}
    </div>
  );
};

export const RobbieDockedPanel: React.FC<RobbieDockedPanelProps> = ({
  state = 'waving',
  subtitle = 'Working with you on this RFQ',
  headerAction,
  nextStep,
  working,
  workingFailed = false,
  messages = [],
  resting,
  continueLine,
  unfoldNonce = 0,
  typing = false,
  historyLoading = false,
  chipsLoading = false,
  notice,
  quickActions = [],
  input,
  footerNote,
  className = '',
}) => {
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, unfoldNonce]);

  return (
    <aside className={`flex h-full shrink-0 flex-col border-l border-robbie-border bg-ai-panel ${className}`} aria-label="Robbie">
      <div className="flex shrink-0 items-center gap-s4 border-b border-robbie/[0.12] px-s5 py-[20px]">
        <RobbieMark size="sm" state={state} aria-label={`Robbie — ${state}`} />
        <div className="flex min-w-0 flex-col">
          <span className="text-body-md font-bold leading-[1.2] text-ink">Robbie</span>
          <span className="truncate text-[10.5px] font-medium text-[#8A8FA8]">{subtitle}</span>
        </div>
        {headerAction && (
          <button
            type="button"
            onClick={headerAction.onSelect}
            aria-label={headerAction.label}
            title={headerAction.label}
            className="ml-auto flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-control
              text-ink-muted transition-colors duration-100 hover:bg-surface hover:text-action focus:outline-none
              focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1"
          >
            <SquarePen className="h-[14px] w-[14px]" />
          </button>
        )}
      </div>
      {working && workingFailed ? (
        <div className="shrink-0 border-b border-robbie/[0.12] px-[12px] py-[11px]">
          <div className="mb-s2 pl-[2px] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#8A8FA8]">Something went wrong</div>
          <div
            role="alert"
            className="flex w-full items-center gap-[9px] rounded-[9px] border border-danger-border bg-danger-bg px-[11px] py-[9px]"
          >
            <AlertCircle className="h-[14px] w-[14px] shrink-0 text-danger-ink" aria-hidden />
            <span className="text-body-sm font-semibold leading-[1.3] text-ink">{working}</span>
          </div>
        </div>
      ) : working ? (
        <div className="shrink-0 border-b border-robbie/[0.12] px-[12px] py-[11px]">
          <div className="mb-s2 pl-[2px] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#8A8FA8]">Working on it</div>
          <div
            role="status"
            className="flex w-full items-center gap-[9px] rounded-[9px] border border-robbie-border bg-surface px-[11px] py-[9px]"
          >
            <RobbieMark size="xs" state="working" aria-label="Robbie is working" />
            <span className="text-body-sm font-medium leading-[1.3] text-ink-body">{working}</span>
          </div>
        </div>
      ) : (
        nextStep && (
          <div className="shrink-0 border-b border-robbie/[0.12] px-[12px] py-[11px]">
            <div className="mb-s2 pl-[2px] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#8A8FA8]">Next best step</div>
            <button
              type="button"
              onClick={nextStep.onSelect}
              className="flex w-full items-center gap-[9px] rounded-[9px] border border-robbie-border bg-surface px-[11px] py-[9px] text-left hover:border-robbie"
            >
              <CheckCircle2 className="h-[14px] w-[14px] shrink-0 text-robbie" />
              <span className="text-body-sm font-semibold leading-[1.3] text-ink">{nextStep.label}</span>
              <ChevronRight className="ml-auto h-[13px] w-[13px] shrink-0 text-[#C0BBD9]" />
            </button>
          </div>
        )
      )}
      <div ref={threadRef} className="flex flex-1 flex-col gap-[9px] overflow-y-auto p-[12px]">
        {historyLoading ? <HistorySkeleton /> : resting ?? (
          <ThreadReveal nonce={unfoldNonce}>
            {messages.map((m) =>
              m.role === 'user' ? (
                <div
                  key={m.id}
                  className={
                    m.variant === 'voice'
                      ? // Wider than a text bubble on purpose: a waveform needs run to
                        // be read, and the rail is narrow.
                        'w-[94%] max-w-[290px] self-end rounded-card border border-line-strong bg-surface px-[11px] py-[9px]'
                      : 'max-w-[94%] self-end rounded-card bg-action px-[12px] py-s4 text-body-sm leading-[1.55] text-white'
                  }
                >
                  {m.content}
                </div>
              ) : (
                <div
                  key={m.id}
                  className="max-w-[94%] rounded-card border border-robbie-border-soft bg-surface px-[12px] py-s4 text-body-sm leading-[1.55] text-ink-body"
                >
                  {m.content}
                </div>
              ),
            )}
          </ThreadReveal>
        )}
        {typing && <TypingDots />}
        {notice}
      </div>
      <div className="flex shrink-0 flex-col gap-s3 border-t border-robbie/[0.12] px-[12px] py-[11px]">
        {continueLine}
        {footerNote ? (
          <p className="m-0 text-[11px] leading-[1.5] text-[#4B4570]">{footerNote}</p>
        ) : (
          <>
            {chipsLoading && quickActions.length === 0 && <ChipSkeletons />}
            {quickActions.length > 0 && (
              <div className="flex gap-s2 overflow-x-auto pb-[2px]">
                {quickActions.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={a.onSelect}
                    className="shrink-0 whitespace-nowrap rounded-round border border-robbie-border bg-surface px-s4 py-[5px] text-[11px] font-medium text-[#4B4570] hover:border-robbie"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
            <RobbieChatInput dense {...input} />
          </>
        )}
      </div>
    </aside>
  );
};

export default RobbieDockedPanel;
